import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { confirmAlert } from "react-confirm-alert";
import {
  Table,
  Input,
  Breadcrumb,
  Button,
  Space,
  Select,
  Popconfirm,
  Tooltip,
  Row,
} from "antd";
import ButtonField from "../../../common/htmlComponents/ButtonField";
import { ReactComponent as Archive } from "../../../image/Archive.svg";
import { ReactComponent as Archiveicon } from "../../../image/Archiveicon.svg";
import { ReactComponent as Filtericon } from "../../../image/FilterIcon.svg";
import { ReactComponent as Editicon } from "../../../image/Editicon.svg";
import SearchIcon from "../../../image/SearchIcon.svg";
import PlusSquareOutlined from "../../../image/PlusSquareOutlined.svg";
import RupeesIcon from "../../../image/RupeesIcon.svg";
import { getRequest, patchRequest, postRequest } from "../../../axios";
import { ReloadOutlined } from "@ant-design/icons";
import { authUserData } from "../../../utils/Helper";
import {
  ErrorNotificationMsg,
  SuccessNotificationMsg,
} from "../../../utils/Notifications";
import { debounce } from "lodash";
import "antd/dist/antd.min.css";
import "../../../styles/Main.scss";

const SubscriptionList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  const hasPermission = authUserData()?.role[0].name;
  const { Search } = Input;
  const [selectedRow, setSelectedRow] = useState([]);
  const [loading, setLoading] = useState(false);
  const [subscriptionList, setSubscriptionList] = useState([]);
  const [totalCount, settotalCount] = useState(0);
  const [reload, setReload] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [currencySymbol, setCurrencySymbol] = useState("₹");
  const [state, setState] = useState({
    search: "",
    page: 1,
    ordering: "id",
    pagesize: 10
  });

  useEffect(() => {
    const getSubscriptionList = async () => {
      setLoading(true);

      try {
        let apiResponse = await getRequest("subscription/", state);

        if (apiResponse.data.results.success) {
          setSubscriptionList(apiResponse.data.results.data);
          settotalCount(apiResponse.data.count);
        } else {
          ErrorNotificationMsg(t("error_in_subscription_list"));
        }
        setLoading(false);
      } catch (error) {
        setLoading(false);
      }
    };
    getSubscriptionList();

    if (hasPermission !== "Super Admin") {
      getCurrency();
    }
  }, [state, reload, t]);

  const handleTableChange = async (pagination, filters, sorter, ordering) => {
    const orderBy = GetSortingEnumValue(sorter.columnKey, sorter.order);
    setState({
      ...state,
      page: pagination.current,
      pagesize: pagination.pageSize,
      ordering: orderBy,
    });
    setSelectedRow(selectedRow);
  };

  const GetSortingEnumValue = (sortField, sortOrder) => {
    let sortingEnumKey = "id";

    if (sortField === "package_name" && sortOrder === "ascend") {
      sortingEnumKey = "package_name";
    }
    if (sortField === "package_name" && sortOrder === "descend") {
      sortingEnumKey = "-package_name";
    }
    if (sortField === "subscription_type" && sortOrder === "ascend") {
      sortingEnumKey = "subscription_type";
    }
    if (sortField === "subscription_type" && sortOrder === "descend") {
      sortingEnumKey = "-subscription_type";
    }

    if (sortField === "per_user_price" && sortOrder === "ascend") {
      sortingEnumKey = "per_user_price";
    }
    if (sortField === "per_user_price" && sortOrder === "descend") {
      sortingEnumKey = "-per_user_price";
    }

    if (sortField === "discount" && sortOrder === "ascend") {
      sortingEnumKey = "discount";
    }
    if (sortField === "discount" && sortOrder === "descend") {
      sortingEnumKey = "-discount";
    }

    if (sortField === "sell_price" && sortOrder === "ascend") {
      sortingEnumKey = "sell_price";
    }
    if (sortField === "sell_price" && sortOrder === "descend") {
      sortingEnumKey = "-sell_price";
    }

    if (sortField === "duration" && sortOrder === "ascend") {
      sortingEnumKey = "duration";
    }
    if (sortField === "duration" && sortOrder === "descend") {
      sortingEnumKey = "-duration";
    }

    if (sortField === "description" && sortOrder === "ascend") {
      sortingEnumKey = "description";
    }
    if (sortField === "description" && sortOrder === "descend") {
      sortingEnumKey = "-description";
    }

    if (sortField === "status" && sortOrder === "ascend") {
      sortingEnumKey = "subscriptionfeature__feature_status";
    }
    if (sortField === "status" && sortOrder === "descend") {
      sortingEnumKey = "-subscriptionfeature__feature_status";
    }

    return sortingEnumKey;
  };
  const handleStatus = async (user_id, status) => {
    confirmAlert({
      title: t("confirmAlert_title"),
      message: t("confirmAlert_message"),
      buttons: [
        {
          label: t("confirmAlert_ok"),
          onClick: () => changeStatus(user_id, status),
        },
        {
          label: t("confirmAlert_cancel"),
          onClick: () => navigate("/subscription"),
        },
      ],
    });
  };

  const changeStatus = async (id, value) => {
    await patchRequest(`subscription/${id}/subscription-status/`, {
      status: value,
    })
      .then((response) => {
        SuccessNotificationMsg(t("status_updated_sucessfully"));
        setReload(!reload);
      })
      .catch((error) => { });
  };

  const searchCallbackDelayed = (event) => {
    const { value } = event.target;
    debounceLoadData(value, onSearch);
  };

  const onSearch = (value) => {
    setSearchText(value); // Update search text
    setState({
      ...state,
      search: value || "", // Update the search filter in state
    });
  };

  const rowSelection = {
    onSelect: (record, selected, selectedRows) => {
      const updatedSelectedRowKeys = selected
        ? [...selectedRow, record.id]
        : selectedRow.filter((key) => key !== record.id);
      setSelectedRow(updatedSelectedRowKeys);
    },
    onSelectAll: (selected, selectedRows, changeRows) => {
      const updatedSelectedRowKeys = selected
        ? [...selectedRow, ...changeRows.map((row) => row.id)]
        : selectedRow.filter(
          (key) => !changeRows.find((row) => row.id === key)
        );
      setSelectedRow(updatedSelectedRowKeys);
    },
    selectedRowKeys: selectedRow,
  };

  const debounceLoadData = debounce((value, onSearch) => {
    onSearch(value);
  }, 1000);

  const handleArchive = async (id = "") => {
    let ids = selectedRow;
    if (ids.length === 0) {
      ids.push(id);
    }
    try {
      let apiResponse = await postRequest("subscription-delete/", {
        deleted: ids,
      });
      if (apiResponse.data.success) {
        let totalArchive = totalCount - ids?.length;

        if (totalArchive < 11) {
          setState((prevState) => ({
            ...prevState,
            page: 1,
            search: "",
          }));
          setReload(!reload);
        } else {
          setReload(!reload);
        }
        SuccessNotificationMsg(t("subscription_archive_successfully"));

        setSelectedRow([]);
      } else {
        setState((prevState) => ({
          ...prevState,
          page: 1,
          search: "",
        }));
        ErrorNotificationMsg(t("error_in_archive_subscription"));
      }
    } catch (error) {
      setState((prevState) => ({
        ...prevState,
        page: 1,
        search: "",
      }));
    }
  };
 

  const getCurrency = async (data) => {
    await getRequest(
      "business-settings/get-currency-by-business-setting/",
      data
    ).then((response) => {
      setCurrencySymbol(response.data.data);
    });
  };

  const columns = [
    {
      title: "Package Name",
      dataIndex: "package_name",
      key: "package_name",
      sorter: true,
    },
    {
      title: "Subscription Type",
      dataIndex: "subscription_type",
      key: "subscription_type",
      sorter: true,
    },
    {
      //title: "Per User Price (₹)",
      title: `Per User Price (${currencySymbol})`,
      dataIndex: "per_user_price",
      key: "per_user_price",
      // render: (per_user_price) => (
      //   <>
      //     <img src={RupeesIcon} alt="RupeesIcon" width={7} /> {per_user_price}{" "}
      //   </>
      // ),
      render: (value) => (
        <span>
          {currencySymbol} {value}
        </span>
      ),
      sorter: true,
    },
    {
      title: "Discount (%)",
      dataIndex: "discount",
      key: "discount",
      render: (discount) => <>{discount} %</>,
      sorter: true,
    },
    {
      title: `Sell Price (${currencySymbol})`,
      dataIndex: "sell_price",
      key: "sell_price",
      // render: (sell_price) => (
      //   <>
      //     <img src={RupeesIcon} alt="RupeesIcon" width={7} /> {sell_price}{" "}
      //   </>
      // ),
      render: (value) => (
        <span>
          {currencySymbol} {value}
        </span>
      ),
      sorter: true,
    },
    {
      title: "Duration (Days)",
      dataIndex: "duration",
      key: "duration",
      render: (duration) => <>{duration} days</>,
      sorter: true,
    },

    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      sorter: true,
    },

    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (e, record) => (
        <div className="SelectStatusSec">
          <Select
            value={e ? e : record.status}
            onChange={(e) => handleStatus(record.id, e)}
            defaultValue={e}
          >
            <Select.Option value="active">
              <div className="dealdoneopt">{t("active")}</div>
            </Select.Option>
            <Select.Option value="in_active">
              <div className="contactedopt">{t("inactive")}</div>
            </Select.Option>
          </Select>
        </div>
      ),
    },
    {
      title: "Action",
      key: "operation",
      fixed: "right",
      width: 100,
      render: (text, record) => (
        <Space>
          <Link
            to={{
              pathname: "/subscription/edit/" + btoa(record.id),
            }}
          >
            {authUserData()?.permission.includes(
              "subcription|change_subcription"
            ) && <Editicon />}
          </Link>

          <Popconfirm
            className="action"
            title={t("pop_title")}
            okText={t("pop_ok")}
            cancelText={t("pop_cancel")}
            onConfirm={() => handleArchive(record.id)}
          >
            {authUserData()?.permission.includes(
              "subcription|delete_subcription"
            ) && <Archive style={{ cursor: "pointer" }} />}
          </Popconfirm>
        </Space>
      ),
    },
  ];
  const colors = ["#094E8F"];
  return (
    <>
      <div className="ListingTopBar">
        <div className="pagenamewrap">
          <div className="pagename">
            <Tooltip
              color={colors}
              placement="right"
              title={
                <span style={{ color: "#fff" }}>
                  Access a comprehensive overview of subscriptions status ,
                  including subscription package name, subscription types, and
                  per user price and discount etc.
                </span>
              }
            >
              <h3>{t("subscriptionList")}</h3>
            </Tooltip>

            <Breadcrumb className="Breadcrumb-Sec">
              <Breadcrumb.Item>
                <Link to="/dashboard">{t("home")}</Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>{t("subscriptionMaster")}</Breadcrumb.Item>
            </Breadcrumb>
          </div>
          <Space>
            <div className="searchwrp">
              <Space direction="vertical">
              <Search
  value={searchText}
  onChange={(e) => {
    const value = e.target.value;
    setSearchText(value);
    debounceLoadData(value, onSearch);
  }}
  allowClear
  onSearch={(value) => {
    setSearchText(value);
    onSearch(value);
  }}
  prefix={
    <img
      src={SearchIcon}
      alt="SearchIcon"
      className="SearchIcon"
      width={22}
    />
  }
  placeholder={t("pl_search_list")}
  style={{ width: 250 }}
/>

               

              </Space>
           
            </div>
          
            {authUserData()?.permission.includes(
              "subcription|add_subcription"
            ) && (
                <Tooltip
                  color={colors}
                  placement="right"
                  title={
                    <span style={{ color: "#fff" }}>
                     Create a new subscription plan with detailed specifications and pricing.
                    </span>
                  }
                >
                  <Button
                    type="primary"
                    className="BlueShadowBtn"
                    onClick={() => navigate("/subscription/add")}
                    icon={<img src={PlusSquareOutlined} alt="Plus Square" />}
                  >
                    {t("addSubscription")}
                  </Button>
                </Tooltip>
              )}

            {selectedRow.length > 0 && (
              <Popconfirm
                className="action"
                title={t("pop_title")}
                okText={t("pop_ok")}
                cancelText={t("pop_cancel")}
                placement="left"
                onConfirm={() => handleArchive()}
              >
                {authUserData()?.permission.includes(
                  "subcription|delete_subcription"
                ) && (
                    <ButtonField
                      className="BlueShadowBtn"
                      type="primary"
                      htmlType="button"
                      label={t("pl_archive")}
                    />
                  )}
              </Popconfirm>
            )}
            <Tooltip placement="topLeft" title="Archived Records">
              <Link to="/subscription/archived">
                <Archiveicon />
              </Link>
            </Tooltip>
          </Space>
          {/* {authUserData()?.permission.includes(
          "subcription|add_subcription"
        ) &&
          <Button
            type="primary"
            className="BlueShadowBtn"
            onClick={() => navigate("/subscription/add")}
            icon={<img src={PlusSquareOutlined} alt="Plus Square" />}
          >
            {t("addSubscription")}
          </Button>
        } */}
        </div>
      </div>

      <section className="grid-sec tablegridsec">
        <div className="PR-body">
          <Row className="tblheader">
            {/* <Col xs={24} sm={24} md={12} lg={12} xl={12} className="tbl-heading">
            <h1>{t("subscriptionList")}</h1>
          </Col> */}

            {/* <Col xs={24} sm={24} md={12} lg={12} xl={12} className="tbl-action">
            <div className="searchwrp">
              <Space direction="vertical">
                <Search
                  prefix={
                    <img
                      src={SearchIcon}
                      alt="SearchIcon"
                      className="SearchIcon"
                      width={22}
                    />
                  }
                  placeholder={t("pl_search_list")}
                  onChange={searchCallbackDelayed}
                  style={{
                    width: 250,
                  }}
                />
              </Space>
            </div>

            {selectedRow.length > 0 && (
              <Popconfirm
                className="action"
                title={t("pop_title")}
                okText={t("pop_ok")}
                cancelText={t("pop_cancel")}
                placement="left"
                onConfirm={() => handleArchive()}
              >
                {authUserData()?.permission.includes(
                  "subcription|delete_subcription"
                ) &&
                  <ButtonField
                    className="BlueShadowBtn"
                    type="primary"
                    htmlType="button"
                    label={t("pl_archive")}
                  />
                }
              </Popconfirm>
            )}
            <Tooltip placement="topLeft" title="Archived Records">
              <Link to="/subscription/archived">
                <Archiveicon />
              </Link>
            </Tooltip>
          </Col> */}
          </Row>
<div className="tablewrp">
          <Table
            scroll={{
              x: 2500,
            }}
            rowSelection={rowSelection}
            columns={columns}
            rowKey={(record) => record.id}
            dataSource={subscriptionList}
            loading={loading}
            pagination={{
              defaultCurrent: state.page,
              defaultPageSize: state.pagesize,
              total: totalCount,
              showTotal: (total) => `Total ${total} items`,
              showSizeChanger: true,
              locale: {
                items_per_page: 'Items / Page', // Text for the size changer
              },
            }}
            onChange={handleTableChange}
          />
          </div>
        </div>
      </section>
    </>
  );
};
export default SubscriptionList;
